// useAccessibility — persists per-user display preferences (font
// size, motion, contrast, sidebar density, focus outline) and
// pipes them into the DOM so the whole app reacts.
//
// The app uses inline styles almost exclusively, which makes it
// hard to apply user-preferred typography via CSS alone. To sidestep
// that we:
//   1) inject a global <style id="a11y-runtime"> once at boot
//   2) toggle attributes on <html> (data-a11y-*) whenever a pref
//      changes
//   3) the injected style targets those data-attributes with
//      selectors like `html[data-a11y-font="lg"]` and applies:
//        * font-size scaling via `zoom` on <body> (respects layout,
//          works with inline pixel sizes)
//        * `* { animation-duration: 0.01ms !important; }` for
//          reduced-motion
//        * a global filter for high-contrast mode
//        * a stronger `:focus-visible` outline for keyboard nav
//
// Prefs live in localStorage under `a11y:v1`. Version bump lets us
// invalidate old shapes cleanly if we grow the schema.

import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'a11y:v1';

export const DEFAULTS = {
  fontSize:      'normal',   // 'sm' | 'normal' | 'lg' | 'xl'
  reducedMotion: 'auto',     // 'auto' | 'on' | 'off'
  highContrast:  false,
  focusRing:     'default',  // 'default' | 'bold'
  density:       'comfortable', // 'compact' | 'comfortable'
};

function loadPrefs() {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

function savePrefs(prefs) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
}

const RUNTIME_STYLE_ID = 'a11y-runtime';
const RUNTIME_CSS = `
/* Font-size scaling. 'zoom' is a non-standard but universally-supported
   property that scales layout, unlike 'transform: scale()' which only
   scales the visual output and leaves layout at 100%. */
html[data-a11y-font="sm"]  body { zoom: 0.92; }
html[data-a11y-font="lg"]  body { zoom: 1.12; }
html[data-a11y-font="xl"]  body { zoom: 1.24; }

/* Motion — flatten animation + transition durations when the user
   asks for it, or when the OS asks for it. */
html[data-a11y-motion="on"] *,
html[data-a11y-motion="on"] *::before,
html[data-a11y-motion="on"] *::after {
  animation-duration:   0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration:  0.01ms !important;
  scroll-behavior:      auto !important;
}

/* High-contrast — invert light-on-dark surfaces via a saturated
   filter. Images/photos are exempted via a nested rule. */
html[data-a11y-contrast="high"] {
  filter: contrast(1.18) saturate(1.1);
}
html[data-a11y-contrast="high"] img,
html[data-a11y-contrast="high"] video,
html[data-a11y-contrast="high"] canvas {
  filter: contrast(0.86) saturate(0.9);
}

/* Focus ring — a bolder ring for keyboard users. Uses focus-visible
   so mouse clicks don't paint the ring. */
html[data-a11y-focus="bold"] :focus-visible {
  outline: 3px solid #E87722 !important;
  outline-offset: 2px !important;
  border-radius: 4px;
}

/* Density — tighten the spacing on cards/rows when the user asks.
   The rules are additive to existing inline padding; won't break the
   layout but visibly compacts scrollable lists. */
html[data-a11y-density="compact"] .a11y-density-row  { padding-top: 4px !important; padding-bottom: 4px !important; }
html[data-a11y-density="compact"] .a11y-density-card { padding: 8px !important; }
`.trim();

function ensureRuntimeStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(RUNTIME_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = RUNTIME_STYLE_ID;
  el.textContent = RUNTIME_CSS;
  document.head.appendChild(el);
}

function applyPrefsToDom(prefs) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.setAttribute('data-a11y-font',     prefs.fontSize);
  html.setAttribute('data-a11y-focus',    prefs.focusRing);
  html.setAttribute('data-a11y-density',  prefs.density);
  html.setAttribute('data-a11y-contrast', prefs.highContrast ? 'high' : 'normal');

  // Motion: 'auto' means "follow OS setting"; explicit on/off wins.
  const motionOn = prefs.reducedMotion === 'on'
    || (prefs.reducedMotion === 'auto'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  html.setAttribute('data-a11y-motion', motionOn ? 'on' : 'off');
}

export function useAccessibility() {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    ensureRuntimeStyle();
    applyPrefsToDom(prefs);
  }, [prefs]);

  // React to OS-level reduced-motion when the user is on 'auto'.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => applyPrefsToDom(prefs);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [prefs]);

  const update = useCallback((patch) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch };
      savePrefs(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setPrefs(DEFAULTS);
    savePrefs(DEFAULTS);
  }, []);

  return { prefs, update, reset };
}
